import { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  RefreshControl,
  SafeAreaView,
  Image,
  Platform,
} from "react-native";
import { useFocusEffect, useRouter } from "expo-router";
import { getDogs, getFollowedDogs, searchPublicDogs } from "../../lib/api";
import { DogCard } from "../../../components/DogCard";
import { Ionicons } from "@expo/vector-icons";
import type { Dog } from "../../lib/types";

type PublicSearchResult = {
  id: string;
  name: string;
  breed: string;
  registeredName: string | null;
  photo: string | null;
  publicSlug: string | null;
};

export default function DogsScreen() {
  const router = useRouter();
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [followed, setFollowed] = useState<Dog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  async function fetchDogs() {
    try {
      // Pull both in parallel — followed list can quietly fail (e.g. for users
      // on a server older than the Follow feature deploy) without blocking My Dogs.
      const [mine, follows] = await Promise.all([
        getDogs(),
        getFollowedDogs().catch(() => [] as Dog[]),
      ]);
      setDogs(mine);
      setFollowed(follows);
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load dogs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      fetchDogs();
    }, [])
  );

  function onRefresh() {
    setRefreshing(true);
    fetchDogs();
  }

  // Tap behavior diverges by section:
  //  • My Dogs → editable dog detail page
  //  • Dogs I Follow → read-only public profile (no edit buttons, no Log a Run)
  // The slug-based public route accepts publicSlug or falls back to id.
  function openFollowed(dog: Dog) {
    const slug = (dog as any).publicSlug || dog.id;
    router.push(`/dog/${slug}` as any);
  }

  // Search a friend's dog by name to follow. Debounced so we don't fire on
  // every keystroke. Min 2 chars enforced server-side too.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<PublicSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchPublicDogs(q);
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  function openSearchResult(r: PublicSearchResult) {
    const slug = r.publicSlug || r.id;
    router.push(`/dog/${slug}` as any);
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      <ScrollView
        contentContainerStyle={{ padding: 20, paddingBottom: 60 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 12 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        {loading && !refreshing ? (
          <View style={{ alignItems: "center", paddingVertical: 60 }}>
            <Text style={{ color: "#6b7280" }}>Loading dogs...</Text>
          </View>
        ) : (
          <>
            {/* My Dogs section — always rendered. When the user has zero dogs
                we show a compact in-section empty card with the add CTA, but
                the Find a Friend's Dog search below stays available so a
                viewer-only user can follow other dogs without adding their own. */}
            <Text style={{ fontSize: 13, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
              My Dogs
            </Text>
            {dogs.length === 0 ? (
              <View
                style={{
                  alignItems: "center",
                  paddingVertical: 32,
                  paddingHorizontal: 20,
                  backgroundColor: "#ffffff",
                  borderRadius: 16,
                  borderWidth: 2,
                  borderColor: "#e5e7eb",
                  borderStyle: "dashed",
                }}
              >
                <Ionicons name="paw-outline" size={48} color="#d1d5db" />
                <Text style={{ fontSize: 16, fontWeight: "700", color: "#374151", marginTop: 12 }}>
                  No dogs yet
                </Text>
                <Text style={{ fontSize: 13, color: "#6b7280", marginTop: 4, textAlign: "center" }}>
                  Add your first dog to track Fast CAT runs — or just follow others below.
                </Text>
                <TouchableOpacity
                  onPress={() => router.push("/(tabs)/dogs/add" as any)}
                  style={{
                    backgroundColor: "#1B2A4A",
                    paddingHorizontal: 22,
                    paddingVertical: 10,
                    borderRadius: 10,
                    marginTop: 16,
                  }}
                >
                  <Text style={{ color: "#ffffff", fontWeight: "600", fontSize: 14 }}>
                    Add Your First Dog
                  </Text>
                </TouchableOpacity>
              </View>
            ) : (
              dogs.map((dog) => (
                <DogCard
                  key={dog.id}
                  dog={dog}
                  onPress={() => router.push(`/(tabs)/dogs/${dog.id}` as any)}
                />
              ))
            )}

            {/* Find a friend's dog to follow — sits between My Dogs (above) and
                Dogs I Follow (below) so newly-found dogs land right next to the
                follow list. */}
            <View style={{ marginTop: 24 }}>
              <Text style={{ fontSize: 13, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Find a Friend's Dog
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  backgroundColor: "#ffffff",
                  borderRadius: 12,
                  paddingHorizontal: 12,
                  borderWidth: 1,
                  borderColor: "#e5e7eb",
                }}
              >
                <Ionicons name="search" size={18} color="#9ca3af" />
                <TextInput
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder="Search by name, AKC name, or breed…"
                  autoCapitalize="none"
                  autoCorrect={false}
                  placeholderTextColor="#9ca3af"
                  style={{ flex: 1, fontSize: 15, paddingVertical: 12, paddingHorizontal: 8, color: "#111827" }}
                />
                {searchQuery.length > 0 && (
                  <TouchableOpacity onPress={() => setSearchQuery("")} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons name="close-circle" size={18} color="#9ca3af" />
                  </TouchableOpacity>
                )}
              </View>
              {searchQuery.trim().length >= 2 && (
                <View style={{ backgroundColor: "#ffffff", borderRadius: 12, marginTop: 8, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" }}>
                  {searching && searchResults.length === 0 ? (
                    <Text style={{ padding: 14, color: "#6b7280", fontSize: 14, textAlign: "center" }}>Searching…</Text>
                  ) : searchResults.length === 0 ? (
                    <Text style={{ padding: 14, color: "#6b7280", fontSize: 14, textAlign: "center" }}>No public dogs found for "{searchQuery.trim()}".</Text>
                  ) : (
                    searchResults.map((r, i) => (
                      <TouchableOpacity
                        key={r.id}
                        onPress={() => openSearchResult(r)}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          padding: 12,
                          gap: 12,
                          borderTopWidth: i === 0 ? 0 : 1,
                          borderTopColor: "#f3f4f6",
                        }}
                      >
                        {r.photo ? (
                          Platform.OS === "web" ? (
                            // @ts-ignore web-only <img>
                            <img src={r.photo} alt={r.name} style={{ width: 40, height: 40, borderRadius: 20, objectFit: "cover" } as any} />
                          ) : (
                            <Image source={{ uri: r.photo }} style={{ width: 40, height: 40, borderRadius: 20 }} />
                          )
                        ) : (
                          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: "#e5e7eb", alignItems: "center", justifyContent: "center" }}>
                            <Ionicons name="paw" size={20} color="#9ca3af" />
                          </View>
                        )}
                        <View style={{ flex: 1 }}>
                          <Text style={{ fontSize: 15, fontWeight: "700", color: "#111827" }}>{r.name}</Text>
                          <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }} numberOfLines={1}>
                            {r.breed}{r.registeredName ? ` • ${r.registeredName}` : ""}
                          </Text>
                        </View>
                        <Ionicons name="chevron-forward" size={18} color="#9ca3af" />
                      </TouchableOpacity>
                    ))
                  )}
                </View>
              )}
            </View>

            {/* Dogs I Follow section — only when at least one. Sits below the
                search section so newly-followed dogs appear right below the
                search bar that found them. */}
            {followed.length > 0 && (
              <View style={{ marginTop: 24 }}>
                <Text style={{ fontSize: 13, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                  Dogs I Follow
                </Text>
                {followed.map((dog) => (
                  <DogCard
                    key={dog.id}
                    dog={dog}
                    onPress={() => openFollowed(dog)}
                  />
                ))}
              </View>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
